import React, { useState, useEffect } from 'react';
import {
    X,
    FileSpreadsheet,
    CheckSquare,
    Square,
    Download,
    Loader2,
    AlertCircle,
    Calendar,
    Building2,
    CheckCircle
} from 'lucide-react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../context/AuthContext';
import { companyAPI } from '../services/api';
import { ediService } from '../services/ediService';
import { Company, EDIExportType, EDIExportRequest } from '../types';

interface EDIExportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ExportTypeOption {
    id: EDIExportType;
    labelKey: string;
    descriptionKey: string;
}

const EDIExportModal: React.FC<EDIExportModalProps> = ({ isOpen, onClose }) => {
    const { t, isRTL } = useI18n();
    const { user } = useAuth();

    // State
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedExportTypes, setSelectedExportTypes] = useState<EDIExportType[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [loadingCompanies, setLoadingCompanies] = useState(false);

    // Export type options
    const exportTypes: ExportTypeOption[] = [
        { id: 'FEC', labelKey: 'edi.types.fec', descriptionKey: 'edi.types.fecDesc' },
        { id: 'TIERS', labelKey: 'edi.types.tiers', descriptionKey: 'edi.types.tiersDesc' },
        { id: 'TVA', labelKey: 'edi.types.tva', descriptionKey: 'edi.types.tvaDesc' },
        { id: 'STATS', labelKey: 'edi.types.stats', descriptionKey: 'edi.types.statsDesc' },
    ];

    // Set default dates (current month)
    useEffect(() => {
        if (isOpen) {
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            setStartDate(firstDay.toISOString().split('T')[0]);
            setEndDate(lastDay.toISOString().split('T')[0]);
            setError(null);
            setSuccess(false);
        }
    }, [isOpen]);

    // Load companies
    useEffect(() => {
        const loadCompanies = async () => {
            if (!isOpen) return;

            setLoadingCompanies(true);
            try {
                const companiesList = await companyAPI.getAllCompanies();
                setCompanies(companiesList);
                // Pre-select all companies
                setSelectedCompanyIds(companiesList.map(c => c.id));
            } catch (err) {
                console.error('Failed to load companies:', err);
                setError(t('edi.error.loadCompanies', { defaultValue: 'Failed to load companies' }));
            } finally {
                setLoadingCompanies(false);
            }
        };

        loadCompanies();
    }, [isOpen, t]);

    // Toggle company selection
    const toggleCompany = (companyId: string) => {
        if (selectedCompanyIds.includes(companyId)) {
            setSelectedCompanyIds(selectedCompanyIds.filter(id => id !== companyId));
        } else {
            setSelectedCompanyIds([...selectedCompanyIds, companyId]);
        }
    };

    // Toggle export type selection
    const toggleExportType = (typeId: EDIExportType) => {
        if (selectedExportTypes.includes(typeId)) {
            setSelectedExportTypes(selectedExportTypes.filter(id => id !== typeId));
        } else {
            setSelectedExportTypes([...selectedExportTypes, typeId]);
        }
    };

    // Select/deselect all companies
    const toggleAllCompanies = () => {
        if (selectedCompanyIds.length === companies.length) {
            setSelectedCompanyIds([]);
        } else {
            setSelectedCompanyIds(companies.map(c => c.id));
        }
    };

    // Select/deselect all export types
    const toggleAllExportTypes = () => {
        if (selectedExportTypes.length === exportTypes.length) {
            setSelectedExportTypes([]);
        } else {
            setSelectedExportTypes(exportTypes.map(t => t.id));
        }
    };

    // Handle export
    const handleExport = async () => {
        if (selectedCompanyIds.length === 0) {
            setError(t('edi.error.noCompanies', { defaultValue: 'Please select at least one company' }));
            return;
        }
        if (selectedExportTypes.length === 0) {
            setError(t('edi.error.noExportTypes', { defaultValue: 'Please select at least one export type' }));
            return;
        }
        if (!startDate || !endDate) {
            setError(t('edi.error.noDates', { defaultValue: 'Please select start and end dates' }));
            return;
        }
        if (startDate > endDate) {
            setError(t('edi.error.invalidDates', { defaultValue: 'Start date must be before end date' }));
            return;
        }

        setIsExporting(true);
        setError(null);
        setSuccess(false);

        try {
            const request: EDIExportRequest = {
                companyIds: selectedCompanyIds,
                startDate: startDate,
                endDate: endDate,
                exportTypes: selectedExportTypes,
            };

            await ediService.exportEDI(request);
            setSuccess(true);

            // Auto-close after success
            setTimeout(() => {
                onClose();
            }, 2000);
        } catch (err: any) {
            console.error('EDI export failed:', err);
            setError(err.message || t('edi.error.exportFailed', { defaultValue: 'Export failed. Please try again.' }));
        } finally {
            setIsExporting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
            <div
                className={`bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] ${isRTL ? 'rtl' : 'ltr'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {t('edi.title', { defaultValue: 'EDI Export' })}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {t('edi.subtitle', { defaultValue: 'Export accounting data in XML format' })}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 overflow-y-auto space-y-6">
                    {/* Date Range Section */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Calendar className="w-4 h-4" />
                            {t('edi.period', { defaultValue: 'Export Period' })}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                    {t('invoices.startDate', { defaultValue: 'Start Date' })}
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    disabled={isExporting}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">
                                    {t('invoices.endDate', { defaultValue: 'End Date' })}
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    disabled={isExporting}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:opacity-50"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Companies Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                <Building2 className="w-4 h-4" />
                                {t('edi.companies', { defaultValue: 'Companies' })}
                                <span className="text-gray-400">({selectedCompanyIds.length}/{companies.length})</span>
                            </div>
                            <button
                                onClick={toggleAllCompanies}
                                disabled={isExporting || loadingCompanies}
                                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
                            >
                                {selectedCompanyIds.length === companies.length
                                    ? t('common.none', { defaultValue: 'Deselect all' })
                                    : t('common.all', { defaultValue: 'Select all' })}
                            </button>
                        </div>

                        {loadingCompanies ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                                <span className="ml-2 text-gray-500">{t('common.loading', { defaultValue: 'Loading...' })}</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                                {companies.map((company) => {
                                    const isSelected = selectedCompanyIds.includes(company.id);
                                    return (
                                        <div
                                            key={company.id}
                                            onClick={() => !isExporting && toggleCompany(company.id)}
                                            className={`
                                                flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer
                                                ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-gray-200'}
                                                ${isExporting ? 'opacity-70 cursor-not-allowed' : ''}
                                            `}
                                        >
                                            {isSelected ? (
                                                <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0" />
                                            ) : (
                                                <Square className="w-5 h-5 text-gray-300 shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium truncate ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                                                    {company.name}
                                                </p>
                                                {company.ice && (
                                                    <p className="text-xs text-gray-400 truncate">ICE: {company.ice}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Export Types Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                <FileSpreadsheet className="w-4 h-4" />
                                {t('edi.exportTypes', { defaultValue: 'Export Types' })}
                                <span className="text-gray-400">({selectedExportTypes.length}/{exportTypes.length})</span>
                            </div>
                            <button
                                onClick={toggleAllExportTypes}
                                disabled={isExporting}
                                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
                            >
                                {selectedExportTypes.length === exportTypes.length
                                    ? t('common.none', { defaultValue: 'Deselect all' })
                                    : t('common.all', { defaultValue: 'Select all' })}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {exportTypes.map((exportType) => {
                                const isSelected = selectedExportTypes.includes(exportType.id);
                                return (
                                    <div
                                        key={exportType.id}
                                        onClick={() => !isExporting && toggleExportType(exportType.id)}
                                        className={`
                                            flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer
                                            ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-gray-200'}
                                            ${isExporting ? 'opacity-70 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        {isSelected ? (
                                            <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                        ) : (
                                            <Square className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />
                                        )}
                                        <div>
                                            <p className={`font-medium ${isSelected ? 'text-emerald-900' : 'text-gray-700'}`}>
                                                {t(exportType.labelKey, { defaultValue: exportType.id })}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t(exportType.descriptionKey, { defaultValue: '' })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <div className="p-4 bg-green-50 border border-green-100 rounded-xl flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-green-700">
                                {t('edi.success', { defaultValue: 'Export completed successfully! Your file is downloading.' })}
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isExporting}
                        className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                    >
                        {t('common.cancel', { defaultValue: 'Cancel' })}
                    </button>
                    <button
                        onClick={handleExport}
                        disabled={isExporting || selectedCompanyIds.length === 0 || selectedExportTypes.length === 0}
                        className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:shadow-none flex items-center gap-2"
                    >
                        {isExporting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('edi.exporting', { defaultValue: 'Exporting...' })}
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                {t('edi.export', { defaultValue: 'Export XML' })}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EDIExportModal;
